import api from "./api";

// Facility booking payments directory (system-user / RBAC — gated by
// "view_payments" on the backend). There is no GET /api/payments/{id} detail
// route or a payment-status-update route despite "view_payment_details" /
// "update_payment_status" being seeded permissions, so this page is
// intentionally a view-only list (the list row already carries every field
// the backend returns).

// GET /api/payments — optional { search, payer_id, payment_status_id }
// filters. Returns a plain array of payments: {id, payment_reference,
// payer_id, payer_code, payer_title, payer_initials,
// payer_name_denoted_by_initials, payer_lastname, payer_email, club_id,
// club_code, club_name, payable_amount, paid_amount, payment_status_id,
// payment_status, paid_at, created_at, updated_at}.
export const listPayments = (params = {}) =>
  api.get("/api/payments", { params }).then((r) => (Array.isArray(r.data) ? r.data : []));

// GET /api/clubs/{id}/payments (PaymentController::forClub) — every payment
// tied to this club, gated by "view_payments" (plus
// "view_club_renewal_payments"/"view_renewal_payments" for those two
// sub-types specifically, same as the /api/payments merge above). Unlike
// listPayments({ search: club_code }) above, this is a purpose-built
// club-scoped merge — it correctly covers all six payment_for types
// (club_registration, member_registration, club_renewal, renewal, reprint,
// booking) matched by the club's numeric id on each table, not just the
// three types that happen to carry a club_id column reachable by a generic
// search term. Response is wrapped ({club_id, club_code, club_name, reg_no,
// primary_phone_number, no_of_coaches, payments: [...]}); this unwraps to
// just the array, same row shape as listPayments (see PAYMENT_TYPE_BADGE /
// paymentDescription() in MembersManagement.jsx for how to render the
// payment_for discriminator).
export const getClubPayments = (clubId) =>
  api.get(`/api/clubs/${clubId}/payments`).then((r) => r.data?.payments || []);

// ── Manual ("shroff" counter) registration-payment reconciliation ──────────
// A separate resource from the facility-booking /api/payments above — these
// look up a member_registration_process / club_registration_process by its
// payment_reference so an instructor can record a payment taken in person at
// the shroff counter instead of through the CyberExt gateway (see
// cyberGatewayService.js for the online path). Two independent pairs of
// routes (members vs clubs) rather than the combined
// /registration-payments/manual-search + /manual-confirm, matching how the
// Members and Clubs pages each drive their own "Confirm Payment" tab.
// Search is gated by "view_payments"; submitting a receipt is gated by
// "submit_manual_payment_receipts" — see RegistrationPaymentController::
// manualMemberSearch/manualMemberConfirm/manualClubSearch/manualClubConfirm.
//
// manualMemberConfirm/manualClubConfirm confirm the payment immediately (the
// underlying registration/club is finalized right away) and separately queue
// a manual_payment_receipts row for a Director/Admin to acknowledge via the
// manual-payment-approvals endpoints below (ManualPaymentApprovalController).
// That acknowledge/reject step is audit-only now — it doesn't gate the
// payment, which has already completed by the time it runs.
//
// Every manual-confirm route (member/club registration here, plus renewals,
// club renewals, ID card reprints and staff bookings in their own service
// files) now requires a scanned `receipt` file alongside receipt_reference —
// pdf/jpg/jpeg/png/webp, max 5MB (ManualPaymentReceiptFile on the backend) —
// so they're all sent as multipart via toManualPaymentFormData() below.

// Builds the multipart body for any */manual-confirm route. Skips
// undefined/null/"" so optional fields like `note` are simply omitted. Don't
// set Content-Type by hand — axios adds multipart/form-data with the boundary
// itself when the body is a FormData.
export const toManualPaymentFormData = (payload) => {
  const formData = new FormData();
  Object.entries(payload).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") formData.append(key, value);
  });
  return formData;
};

// GET /api/registration-payments/members — the full member-registration
// payment ledger (every process with a payment_reference, not just a single
// lookup), gated by "view_payments". Optional { search, payment_status_id }
// filters (RegistrationPaymentController::memberPayments). Distinct from the
// manual-search-by-reference flow below: this is a list, not a lookup — and
// distinct from GET /api/member/payments/registrations (memberService.js),
// which is member-scoped to the caller's own token rather than admin-wide.
// Returns a plain array of rows: {process_type: "member", process_id,
// income_id, income_description, ledger_code, payment_reference,
// external_payment_reference, payable_amount, paid_amount,
// payment_status_id, payment_status, payment_completed, paid_at,
// process_status_id, process_status, finalized, member_id, member_code,
// applicant: {title, initials, name_denoted_by_initials, lastname,
// full_name, email, nic_number, primary_phone_number, requested_club_id,
// requested_club_name, ...}, created_at, updated_at}.
export const listMemberRegistrationPayments = (params = {}) =>
  api.get("/api/registration-payments/members", { params }).then((r) => (Array.isArray(r.data) ? r.data : []));

// GET /api/registration-payments/members/{processId} — single row, same shape as one entry
// from listMemberRegistrationPayments above (RegistrationPaymentController::memberPayment).
export const getMemberRegistrationPayment = (processId) =>
  api.get(`/api/registration-payments/members/${processId}`).then((r) => r.data);

// GET /api/registration-payments/clubs — the club-registration equivalent of
// listMemberRegistrationPayments above (RegistrationPaymentController::clubPayments). Optional
// { search, payment_status_id } filters. Returns a plain array of rows: {process_type: "club",
// process_id, payment_reference, external_payment_reference, payable_amount, paid_amount,
// payment_status_id, payment_status, payment_completed, paid_at, process_status_id,
// process_status, finalized, club_id, club_code, club: {club_name, reg_no,
// primary_phone_number, no_of_coaches}, created_at, updated_at}.
export const listClubRegistrationPayments = (params = {}) =>
  api.get("/api/registration-payments/clubs", { params }).then((r) => (Array.isArray(r.data) ? r.data : []));

// GET /api/registration-payments/clubs/{processId} — single row, same shape as one entry
// from listClubRegistrationPayments above (RegistrationPaymentController::clubPayment).
export const getClubRegistrationPayment = (processId) =>
  api.get(`/api/registration-payments/clubs/${processId}`).then((r) => r.data);

// GET /api/registration-payments/members/manual-search?payment_reference=…
export const searchMemberRegistrationPayment = (paymentReference) =>
  api
    .get("/api/registration-payments/members/manual-search", { params: { payment_reference: paymentReference } })
    .then((r) => r.data.payment);

// POST /api/registration-payments/members/manual-confirm — multipart. payload:
// { payment_reference, paid_amount, paid_date, receipt_reference, receipt
// (File, required), note? }.
// Completes the payment (and finalizes the registration) immediately —
// HTTP 200, not a queued 202. approval_status: "Pending" in the response
// means "awaiting Director acknowledgement," not "awaiting completion" (see
// the manual-payment-approvals section below).
export const confirmMemberRegistrationPayment = (payload) =>
  api
    .post("/api/registration-payments/members/manual-confirm", toManualPaymentFormData(payload))
    .then((r) => r.data);

// GET /api/registration-payments/clubs/manual-search?payment_reference=…
export const searchClubRegistrationPayment = (paymentReference) =>
  api
    .get("/api/registration-payments/clubs/manual-search", { params: { payment_reference: paymentReference } })
    .then((r) => r.data.payment);

// POST /api/registration-payments/clubs/manual-confirm — multipart, same
// shape (receipt file included) and immediate-completion behavior as the
// member version above.
export const confirmClubRegistrationPayment = (payload) =>
  api
    .post("/api/registration-payments/clubs/manual-confirm", toManualPaymentFormData(payload))
    .then((r) => r.data);

// ── Manual payment approval workflow (Director/Admin acknowledgement) ──────
// ManualPaymentApprovalController — reviews the receipts submitted above.
// Each row's payable_type is "member_registration_process" or
// "club_registration_process" (payment_for: "member_registration" /
// "club_registration"); the nested `payable` object carries the applicant/
// club name and payment_reference for display.

// Every row carries has_receipt_file / receipt_file_path / receipt_file_url
// (ManualPaymentReceiptFile::apiFields). Rows submitted before receipts
// became mandatory have has_receipt_file: false — backfill them with
// uploadManualPaymentReceipt() below. Prefer getManualPaymentReceiptFile()
// over receipt_file_url for viewing: the URL is built from the backend's own
// APP_URL and skips the permission check.

// GET /api/manual-payment-approvals — optional { status, search,
// missing_receipt }; status defaults to "Pending" server-side when omitted —
// unless missing_receipt is truthy, in which case it defaults to all
// statuses. Gated by "view_manual_payment_approvals".
export const listManualPaymentApprovals = (params = {}) =>
  api.get("/api/manual-payment-approvals", { params }).then((r) => (Array.isArray(r.data) ? r.data : []));

// GET /api/manual-payment-approvals/{id}
export const getManualPaymentApproval = (id) =>
  api.get(`/api/manual-payment-approvals/${id}`).then((r) => r.data);

// POST /api/manual-payment-approvals/{id}/receipt — multipart { receipt
// (File, required) }. Attaches a receipt to an existing row (legacy
// backfill), or replaces the one already there. Gated by
// "submit_manual_payment_receipts". Returns { message, approval_id,
// has_receipt_file, receipt_file_path, receipt_file_url, receipt: {...full
// row} }.
export const uploadManualPaymentReceipt = (id, file) =>
  api
    .post(`/api/manual-payment-approvals/${id}/receipt`, toManualPaymentFormData({ receipt: file }))
    .then((r) => r.data);

// GET /api/manual-payment-approvals/{id}/receipt — streams the stored file
// (PDF or image). Gated by "view_manual_payment_approvals" OR
// "submit_manual_payment_receipts". 404s with JSON if no file is attached.
// responseType "blob" so axios doesn't try to parse it — which also means an
// error's JSON body arrives as a Blob, so it's parsed back onto
// err.response.data here and callers can read .message as usual.
export const getManualPaymentReceiptFile = (id) =>
  api
    .get(`/api/manual-payment-approvals/${id}/receipt`, { responseType: "blob" })
    .then((r) => r.data)
    .catch(async (err) => {
      const data = err?.response?.data;
      if (data instanceof Blob) {
        try {
          err.response.data = JSON.parse(await data.text());
        } catch {
          // not JSON — leave as-is
        }
      }
      throw err;
    });

// POST /api/manual-payment-approvals/{id}/approve — audit-only acknowledgement.
// The underlying payment already completed at manual-confirm time; this does
// not finalize or unlock anything. Gated by "approve_manual_payment_receipts".
// Returns { message, approval_status: "Acknowledged" }.
export const approveManualPaymentApproval = (id) =>
  api.post(`/api/manual-payment-approvals/${id}/approve`).then((r) => r.data);

// POST /api/manual-payment-approvals/{id}/reject  { rejection_note }. Gated
// by "reject_manual_payment_receipts". Only flags the receipt for review —
// does NOT reverse the payment, which already completed at manual-confirm time.
export const rejectManualPaymentApproval = (id, rejectionNote) =>
  api.post(`/api/manual-payment-approvals/${id}/reject`, { rejection_note: rejectionNote }).then((r) => r.data);

// GET /api/manual-payment-approvals/my-submissions — the receipts *this*
// system user has submitted (any payable type — the route isn't scoped to
// members vs clubs), so an instructor can track their own approval status
// and read the rejection_note if one was rejected. Optional { status,
// search }. Gated by "submit_manual_payment_receipts" (the same permission
// needed to submit a receipt in the first place) — see
// ManualPaymentApprovalController::mySubmissions.
export const listMyManualPaymentSubmissions = (params = {}) =>
  api.get("/api/manual-payment-approvals/my-submissions", { params }).then((r) => (Array.isArray(r.data) ? r.data : []));

// ── Shroff voucher PDF (system-operator download) ──────────────────────────
// Same printable voucher as the public "applicant-shroff-voucher" route used
// pre-login on the "Pay at Counter" instructions page, but authenticated
// (system-user Bearer token) and keyed by processId alone — no
// payment_reference needed since the caller already has admin access. Gated
// by "view_payments" (see RegistrationPaymentController::memberShroffVoucher /
// clubShroffVoucher). responseType "blob" so axios doesn't try to JSON-parse
// the PDF.
export const getMemberShroffVoucherPdf = (processId) =>
  api
    .get(`/api/registration-payments/members/${processId}/shroff-voucher`, { responseType: "blob" })
    .then((r) => r.data);

export const getClubShroffVoucherPdf = (processId) =>
  api
    .get(`/api/registration-payments/clubs/${processId}/shroff-voucher`, { responseType: "blob" })
    .then((r) => r.data);
